"use client";

import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";

// Catches any error thrown while rendering a Server Component or during
// client-side rendering below the root layout — e.g. getInventory25Snapshot()
// throwing on a real DB error. Before this file existed, ANY such error fell
// through to Next.js's bare default fallback ("An error occurred in the
// Server Components render...", no digest visible, no way to retry or report
// it) — reported 2026-08-18. This doesn't fix the underlying error, it just
// gives whoever hits one something actionable instead of a dead end.
export default function GlobalErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[error boundary]", error);
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "var(--bg-base)" }}>
      <div className="w-full max-w-md text-center space-y-4">
        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-950/40">
          <AlertTriangle size={22} className="text-red-600 dark:text-red-300" />
        </div>
        <div>
          <h1 className="text-lg font-bold" style={{ color: "var(--text-1)" }}>Something went wrong</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-3)" }}>
            This page hit an unexpected error. It&apos;s been logged — try again, or head back and let staff know if it keeps happening.
          </p>
        </div>
        {error.digest && (
          <p className="text-xs font-mono" style={{ color: "var(--text-3)" }}>
            Reference: {error.digest}
          </p>
        )}
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            onClick={reset}
            className="px-4 py-2 rounded-lg text-sm font-bold transition-colors"
            style={{ backgroundColor: "var(--amber)", color: "var(--primary-foreground)" }}
          >
            Try again
          </button>
          <a
            href="/"
            className="px-4 py-2 rounded-lg text-sm font-semibold border transition-colors hover:bg-[var(--bg-raised)]"
            style={{ borderColor: "var(--border)", color: "var(--text-2)" }}
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
